import { createPresignedUrlMutationFn } from '../../graphql/mutation/createPresignedUrl';
import { BlobUpload } from './BlobUpload';
import { FileChecksum } from './FileChecksum';

export const INVALID_FILE_TYPE_ERROR = 'INVALID_FILE_TYPE';

const FALLBACK_CONTENT_TYPES = [
  'image/avif',
  'image/heic',
  'image/heif',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
];

export type FileMetaData = {
  checksum: string;
  filename: string;
  content_type: string;
  byte_size: number;
};

export const calculateChecksum = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    FileChecksum.create(file, (error, checksum) => {
      if (error) {
        reject(error);
        return;
      }

      if (checksum) {
        resolve(checksum);
      } else {
        reject('Checksum not generated');
      }
    });
  });

export const getFileMetadata = (file: File): Promise<FileMetaData> =>
  new Promise((resolve, reject) => {
    calculateChecksum(file)
      .then((checksum) => {
        resolve({
          checksum,
          filename: file.name,
          content_type: file.type,
          byte_size: file.size,
        });
      })
      .catch(reject);
  });

export const uploadToUrl = ({
  file,
  url,
  headers,
  fields,
}: {
  file: File;
  url: string;
  headers?: string;
  fields?: string;
}) => {
  if (headers) {
    const upload = new BlobUpload({
      file,
      directUploadData: {
        url,
        headers: { ...JSON.parse(headers) },
      },
    });

    return new Promise((resolve, reject) => {
      upload.create((error, response) => {
        if (error) {
          reject(error);
        } else {
          resolve(response);
        }
      });
    });
  } else if (fields) {
    const fieldsData = JSON.parse(fields);
    const formData = new FormData();
    for (const key in fieldsData) {
      formData.append(key, fieldsData[key]);
    }
    formData.append('file', file);
    return fetch(url, {
      method: 'POST',
      body: formData,
    });
  }
};

export const uploadImage = async ({
  file,
  metadataId,
}: {
  file: File;
  metadataId?: string;
}) => {
  const checksum = await calculateChecksum(file);
  const metadata = await getFileMetadata(file);

  if (!checksum || !metadata) {
    throw new Error('Cannot resolve without file data');
  }

  const {
    createPresignedUrl: { url, headers, fields, id },
  } = await createPresignedUrlMutationFn({
    input: {
      metadataId,
      filename: file.name,
      byteSize: metadata.byte_size,
      checksum,
      contentType: metadata.content_type,
    },
  });

  let allowedTypes = FALLBACK_CONTENT_TYPES;

  if (fields) {
    try {
      const fieldsData = JSON.parse(fields);
      const allowedContentTypes = fieldsData['content-type'];

      if (allowedContentTypes) {
        allowedTypes = allowedContentTypes
          .split(',')
          .map((type: string) => type.trim());
      }
    } catch {
      allowedTypes = FALLBACK_CONTENT_TYPES;
    }
  }

  if (!allowedTypes.includes(file.type.toLowerCase())) {
    throw new Error(INVALID_FILE_TYPE_ERROR);
  }

  try {
    await uploadToUrl({
      file,
      url,
      headers: headers ?? undefined,
      fields: fields ?? undefined,
    });

    return id;
  } catch (error) {
    throw new Error(`An error occurred while uploading images: ${error}`);
  }
};
